import React, { useCallback, useEffect, useState } from 'react'
import Select from 'react-select'
import PropTypes from 'prop-types'
import QSearchObject from '../hooks/qSearchObject'
import { groupBy, map, flatMap, reduce, includes, flatMapDeep, find, isEqual, isEmpty, xorWith } from 'lodash'
import * as Icon from '@material-ui/icons'
import { useIsMountedRef } from '../../../utils/Helper'
let selectedQlikFields = null

const QDocMultiFieldSearch = ({ qDocPromise, qAppPromise, placeholder, cols, qPage, options, getData }) => {
  const isMountedRef = useIsMountedRef()
  const placeholderColumns = map(cols).join(' , ')
  const [qTerm, setQTerm] = useState('')
  const [selected, setSelected] = useState(null)
  const [searchResults, setSearchResults] = useState([])

  const { qLayout, clearSelections } = QSearchObject({
    qDocPromise,
    cols,
    qPage,
    options,
  })
  let selections = []

  const handleCallback = useCallback(() => {
    return getData(qLayout, [getData, qLayout])
  }, [getData, qLayout])
  if (qLayout) {
    const selectedFields = qLayout.qSelectionObject.qSelections
    if (selectedFields.length) {
      selections = selectedFields.map(value => {
        if (value.qSelectedCount >= 1 && value.qSelectedCount <= 6) {
          return {
            field: value.qReadableName || value.qField,
            selected: value.qSelectedFieldSelectionInfo.map(valueInner => valueInner.qName),
            total: value.qTotal,
          }
        }
        if (value.qSelectedCount > 6) {
          return {
            field: value.qField,
            selected: [`${value.qSelectedCount} of ${value.qTotal}`],
            hideOnDelete: true,
          }
        }
        return null
      })
    }
  }

  selectedQlikFields = flatMap(
    selections,
    S =>
      includes(cols, S.field) &&
      map(S.selected, value => {
        return { label: value, value, field: S.field }
      })
  )

  const search = useCallback(async () => {
    const qApp = await qAppPromise
    qApp &&
      qApp.searchResults(
        [qTerm],
        { qOffset: 0, qCount: 7 },
        {
          qSearchFields: cols,
          qContext: 'CurrentSelections',
        },
        function (reply) {
          let results = flatMapDeep(reply.qResult.qSearchGroupArray, group => {
            return map(group.qItems, item => ({
              field: item.qIdentifier,
              values: item.qItemMatches.map(match => match.qText),
            }))
          })
          results = groupSearchResults(results)
          if (isMountedRef.current) {
            setSearchResults(results)
          }
        }
      )
    // eslint-disable-next-line
  }, [cols, qAppPromise, qTerm])

  const groupSearchResults = results => {
    return reduce(
      results,
      (result, value) => {
        //find the label group
        let existingSelectionObject = find(result, label_group => {
          return label_group.label === value.field
        })

        //create a group of options from the current value
        const selectionGroupOptions = map(value.values, match => {
          return {
            label: match,
            value: match,
            field: value.field,
          }
        })

        if (!existingSelectionObject) {
          result.push({ label: value.field, options: selectionGroupOptions })
        } else {
          result = map(result, item => {
            if (item.label === value.field) {
              item.options.push(selectionGroupOptions)
            }
            return item
          })
        }

        return result
      },
      []
    )
  }

  const makeSelectionToQlik = async selectedInput => {
    const qApp = await qAppPromise
    const grouped = groupBy(selectedInput, ({ field }) => field)

    map(grouped, group => {
      const values = map(group, ({ value }) => value)
      const field = group[0].field

      qApp.field(field).clear()
      qApp.field(field).selectValues(values, true, true)
    })
  }

  const MultiValueRemove = props => {
    return (
      <Icon.HighlightOff
        className={'clickable'}
        /* eslint-disable-next-line react/prop-types */
        onClick={() => handleRemoveSelection(props.data)}
        style={{ fontSize: '1.2em', color: '#ffffff', margin: '0.19em' }}
      />
    )
  }

  const ClearIndicator = () => {
    return (
      <Icon.HighlightOff
        className={'clickable'}
        /* eslint-disable-next-line react/prop-types */
        onClick={() => map(cols, field => clearSelections(field))}
        style={{ fontSize: '1.2em', color: '#ffffff', margin: '0.19em' }}
      />
    )
  }

  const handleRemoveSelection = async ({ value, field }) => {
    const qApp = await qAppPromise
    if (qApp) {
      const fieldInCurrentSelections = find(selections, selection => selection.field === field)
      if (fieldInCurrentSelections) {
        clearSelections(field, value)
      }
    }
  }

  useEffect(() => {
    if (qLayout && getData) {
      if (isMountedRef.current) {
        handleCallback()
      }
    }
    // eslint-disable-next-line
  }, [getData, handleCallback, qLayout])

  useEffect(() => {
    if (qTerm) {
      if (isMountedRef.current) {
        search(qTerm)
      }
    }
    // eslint-disable-next-line
  }, [qTerm])

  useEffect(() => {
    if (!isEmpty(xorWith(selectedQlikFields, selected, isEqual))) {
      if (isMountedRef.current) {
        setSelected(selectedQlikFields)
      }
    }
    // eslint-disable-next-line
  }, [selectedQlikFields])

  return (
    isMountedRef.current && (
      <div style={{ width: '100%', display: 'inline-block' }}>
        <Select
          className={'basic-search'}
          classNamePrefix={'select'}
          components={{ MultiValueRemove, DropdownIndicator: null, ClearIndicator }}
          placeholder={!placeholder ? `Search for ${placeholderColumns}` : placeholder}
          value={selected}
          defaultValue={selected}
          style={{
            multiValueRemove: base => ({
              ...base,
              // border: `1px dotted ${colourOptions[2].color}`,
              height: '80%',
              marginTop: '10%',
            }),
            clearIndicator: base => ({
              ...base,
              // border: `1px dotted ${colourOptions[2].color}`,
              height: '100%',
              marginRight: 10,
            }),
          }}
          isMulti
          isClearable={true}
          isSearchable={true}
          onChange={_selected => {
            makeSelectionToQlik(_selected)
          }}
          onInputChange={input => {
            if (isMountedRef.current) {
              setQTerm(input)
            }
          }}
          options={searchResults}
        />
      </div>
    )
  )
}

QDocMultiFieldSearch.propTypes = {
  qDocPromise: PropTypes.object.isRequired,
  qAppPromise: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
  cols: PropTypes.array,
  options: PropTypes.object,
  qPage: PropTypes.object,
  getData: PropTypes.func,
}

QDocMultiFieldSearch.defaultProps = {
  cols: [],
  options: { qType: 'SelectionObject' },
  qPage: {
    qTop: 0,
    qLeft: 0,
    qWidth: 1,
    qHeight: 1,
  },
  getData: null,
}

export default QDocMultiFieldSearch
